import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaShoppingCart, 
  FaUser, 
  FaBars, 
  FaTimes, 
  FaCrown, 
  FaChevronDown 
} from 'react-icons/fa';

const Navbar = ({ isLoggedIn, isAdmin, cartItems, cartCount = 0 }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isDashboardDropdownOpen, setIsDashboardDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Packages', path: '/packages' },
  ];

  const dashboardMenuItems = [
    { name: 'User Dashboard', path: '/dashboard', icon: <FaUser />, description: 'View your orders and profile' },
    ...(isAdmin ? [{ name: 'Admin Panel', path: '/admin', icon: <FaCrown />, description: 'Manage platform and users' }] : []),
  ];

  const authLinks = isLoggedIn
    ? [
        { name: 'Dashboard', path: '#', icon: <FaUser />, isDropdown: true },
      ]
    : [
        { name: 'Login', path: '/login' },
        { name: 'Signup', path: '/signup' },
      ];

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 rounded-b-3x1 ${
      isScrolled 
        ? 'bg-gradient-to-r from-purple-700 to-purple-800 shadow-lg' 
        : 'bg-gradient-to-r from-purple-600 to-purple-700'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-12 h-12 bg-gradient-to-br from-white to-purple-100 rounded-xl flex items-center justify-center transform group-hover:rotate-12 transition-all duration-300 shadow-lg border-2 border-purple-300">
              <span className="text-purple-700 font-bold text-2xl">F</span>
            </div>
            <span className="text-2xl font-bold text-white">FollowersCart</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`font-medium transition-all duration-300 hover:text-white ${
                  location.pathname === link.path
                    ? 'text-white border-b-2 border-white'
                    : 'text-purple-200'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right side buttons */}
          <div className="hidden md:flex items-center space-x-4">

            {/* CART */}
            <div className="relative">
              <button 
                onClick={() => setIsCartOpen(!isCartOpen)}
                className="relative p-2 text-purple-200 hover:text-white transition-colors duration-300"
              >
                <FaShoppingCart className="text-xl" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-white text-purple-600 text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              {isCartOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-purple-100 rounded-lg shadow-xl border border-purple-600 z-50">
                  <div className="p-4">
                    <h3 className="font-semibold text-purple-800 mb-3">
                      Shopping Cart
                    </h3>

                    {cartItems?.length > 0 ? (
                      <>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {cartItems.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-2 border-b border-purple-700">
                              <div>
                                <p className="font-semibold text-purple-800">{item.name}</p>
                                <p className="text-xs text-purple-400">ID: {item._id}</p>
                              </div>

                              <div className="text-right">
                                <span className="font-semibold text-white">
                                  ${item.price}
                                </span>
                                <span className="text-sm text-purple-200">
                                  Qty: {item.quantity}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-4 pt-3 border-t border-gray-200">
                          <div className="flex justify-between items-center mb-3">
                            <span className="font-semibold text-purple-800">Total:</span>
                            <span className="text-xl font-bold text-purple-600">
                              {(
                                cartItems || []
                              ).reduce(
                                (sum, item) =>
                                  sum + item.price * item.quantity,
                                0
                              ).toFixed(2)}
                            </span>
                          </div>

                          <button className="btn-primary w-full">
                            Checkout
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="text-purple-400 text-center py-4">
                        Cart is empty
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* AUTH */}
            {authLinks.map((link) => (
              <div key={link.name} className="relative">
                {link.isDropdown ? (
                  <>
                    <button
                      onClick={() => setIsDashboardDropdownOpen(!isDashboardDropdownOpen)}
                      className="flex items-center space-x-2 font-medium text-purple-200 hover:text-white transition-all duration-300"
                    >
                      {link.icon}
                      <span>{link.name}</span>
                      <FaChevronDown className={`text-xs transition-transform duration-300 ${isDashboardDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {isDashboardDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-64 bg-purple-100 rounded-lg shadow-xl border border-purple-600 z-50">
                        <div className="p-2">
                          {dashboardMenuItems.map((item) => (
                            <Link
                              key={item.name}
                              to={item.path}
                              onClick={() => setIsDashboardDropdownOpen(false)}
                              className="flex items-start space-x-3 p-3 rounded-lg hover:bg-purple-200 transition-colors duration-200"
                            >
                              <div className="flex-shrink-0 text-purple-600 mt-1">
                                {item.icon}
                              </div>
                              <div>
                                <p className="font-semibold text-purple-800">{item.name}</p>
                                <p className="text-xs text-purple-600">{item.description}</p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className="flex items-center space-x-2 font-medium text-purple-200 hover:text-white transition-all duration-300"
                  >
                    {link.icon}
                    <span>{link.name}</span>
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-purple-200 hover:text-white transition-colors duration-300"
          >
            {isMobileMenuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-purple-100 border-t border-purple-600 mt-4">
            <div className="px-2 pt-4 pb-3 space-y-1">
              {/* Mobile Logo */}
              <div className="flex items-center space-x-3 px-3 pb-4 border-b border-purple-300">
                <div className="w-10 h-10 bg-gradient-to-br from-white to-purple-100 rounded-lg flex items-center justify-center border-2 border-purple-300">
                  <span className="text-purple-700 font-bold text-lg">F</span>
                </div>
                <span className="text-lg font-bold text-purple-800">FollowersCart</span>
              </div>

              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-3 py-2 rounded-md font-medium transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'text-purple-600 bg-purple-50'
                      : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              <div className="border-t border-gray-200 pt-4 mt-4">
                {authLinks.map((link) => (
                  <div key={link.name}>
                    {link.isDropdown ? (
                      <>
                        <button
                          onClick={() => setIsDashboardDropdownOpen(!isDashboardDropdownOpen)}
                          className="flex items-center space-x-2 font-medium text-gray-700 hover:text-purple-600 w-full text-left"
                        >
                          {link.icon}
                          <span>{link.name}</span>
                          <FaChevronDown className={`text-xs transition-transform duration-300 ${isDashboardDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isDashboardDropdownOpen && (
                          <div className="ml-4 mt-2 space-y-1">
                            {dashboardMenuItems.map((item) => (
                              <Link
                                key={item.name}
                                to={item.path}
                                onClick={() => {
                                  setIsDashboardDropdownOpen(false);
                                  setIsMobileMenuOpen(false);
                                }}
                                className="flex items-start space-x-2 p-2 rounded-md text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                              >
                                <div className="flex-shrink-0 mt-0.5">
                                  {item.icon}
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{item.name}</p>
                                  <p className="text-xs text-gray-500">{item.description}</p>
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={link.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center space-x-2 font-medium text-gray-700 hover:text-purple-600"
                      >
                        {link.icon && <span>{link.icon}</span>}
                        <span>{link.name}</span>
                      </Link>
                    )}
                  </div>
                ))}
              </div>

            </div>
          </div>
        )}

      </div>
    </nav>
  );
};

export default Navbar;